<template>
  <main>
    <organism-dashboard
      v-if="!isLoading"
      :eventList="eventList"
      :cpuList="cpuList"
      :memoryList="memoryList"
      :storageList="storageList"
      :equipmentResourceList="equipmentResourceList"
    ></organism-dashboard>
    <b-card v-else>
      <div class="d-flex flex-column align-items-center p-3">
        <feather-icon
          icon="CloudOffIcon"
          class="empty-data__icon"
        ></feather-icon>
        <label class="empty-data__text--title">No Data</label>
      </div>
    </b-card>
  </main>
</template>

<script>
import { BCard } from "bootstrap-vue"
import OrganismDashboard from "@/components/organisms/OrganismDashboard.vue"
import {
  getDashboardCpu,
  getDashboardMemory,
  getDashboardStorage,
  getDashboardEquipmentResource,
  getDashboardEvents,
} from "@/api/api-module"
import mixins from "@/mixins"
import mixinsDateTime from "@/mixins/mixinsDatetime"
import { DateTime } from "luxon"
import { mapActions, mapGetters } from "vuex"
export default {
  components: { OrganismDashboard, BCard },
  mixins: [mixins, mixinsDateTime],
  data() {
    return {
      eventList: [],
      cpuList: [],
      memoryList: [],
      storageList: [],
      equipmentResourceList: [],
      dateTimeRange: {
        from: "",
        to: "",
      },
      intervalId: 0,
    }
  },
  computed: {
    ...mapGetters("appAutoRefresh", ["getSMSDashboardAutoRefresh"]),
    isLoading() {
      return (
        !this.cpuList.length &&
        !this.memoryList.length &&
        !this.storageList.length &&
        !this.equipmentResourceList.length &&
        !this.eventList.length
      )
    },
    isEnableAutoRefresh() {
      return this.getSMSDashboardAutoRefresh
    },
  },
  watch: {
    isEnableAutoRefresh: {
      deep: true,
      immediate: true,
      handler() {
        if (this.isEnableAutoRefresh) {
          this.intervalId = window.setInterval(() => {
            this.showLoading(true)
            this.dateTimeRange = {
              from: this.removeTimeZone(
                DateTime.now().minus({ days: 1 }).toISO()
              ),
              to: this.removeTimeZone(DateTime.now().toISO()),
            }
            this.getDashboardAll()
          }, 60000)
        } else {
          window.clearInterval(this.intervalId)
        }
      },
    },
  },
  mounted() {
    this.setIsShowAutoRefresh(true)
    this.dateTimeRange = {
      from: this.removeTimeZone(DateTime.now().minus({ days: 1 }).toISO()),
      to: this.removeTimeZone(DateTime.now().toISO()),
    }
    this.getDashboardAll()
  },
  beforeDestroy() {
    this.setIsShowAutoRefresh(false)
    window.clearInterval(this.intervalId)
  },
  methods: {
    ...mapActions("appAutoRefresh", ["setIsShowAutoRefresh"]),

    getDashboardAll() {
      this.getEventList()
      this.getCpuList()
      this.getMemoryList()
      this.getStorageList()
      this.getEquipmentResourceList()
    },

    async getEventList() {
      this.showLoading(true)
      this.eventList = []
      await getDashboardEvents(this.dateTimeRange.from, this.dateTimeRange.to)
        .then((response) => {
          response.forEach((event) => {
            this.eventList.push({
              id: event.eventId,
              date: DateTime.fromISO(event.timestamp).toFormat("dd/MM/yyyy"),
              time: DateTime.fromISO(event.timestamp).toFormat("HH:mm:ss"),
              equipmentName: event.equipmentName,
              tag: event.tags,
              equipmentIp: event.equipmentIpAddress,
              resources: event.resourceType,
              value:
                parseFloat(event.value) < 0.1
                  ? "< 0.1"
                  : parseFloat(event.value).toFixed(1),
              description: event.equipmentDescription,
              warningValue: parseFloat(event.warningValue).toFixed(2),
              criticalValue: parseFloat(event.criticalValue).toFixed(2),
              policyUnit: event.unit,
              status: event.status,
            })
          })
        })
        .finally(() => this.showLoading(false))
    },

    async getCpuList() {
      this.showLoading(true)
      this.cpuList = []
      await getDashboardCpu()
        .then((response) => {
          response.forEach((cpu) => {
            this.cpuList.push({
              id: cpu.equipmentId,
              equipmentName: cpu.hostname,
              description: cpu.description,
              equipmentIp: cpu.ipAddress,
              currentValue:
                cpu.percentProcessor < 0.1
                  ? "< 0.1"
                  : cpu.percentProcessor.toFixed(1),
              status: cpu.resourceStatus,
              warningValue:
                cpu.policyValue?.policyResourceWarningThreshold.toFixed(2),
              criticalValue:
                cpu.policyValue?.policyResourceCriticalThreshold.toFixed(2),
              policyUnit: cpu.policyValue?.policyResourceUnit,
            })
          })
        })
        .finally(() => {
          this.showLoading(false)
        })
    },

    async getMemoryList() {
      this.showLoading(true)
      this.memoryList = []
      await getDashboardMemory()
        .then((response) => {
          response.forEach((memory) => {
            this.memoryList.push({
              id: memory.equipmentId,
              equipmentName: memory.hostname,
              description: memory.description,
              equipmentIp: memory.ipAddress,
              currentValue:
                memory.memoryPercentUsed < 0.1
                  ? "< 0.1"
                  : memory.memoryPercentUsed.toFixed(1),
              status: memory.resourceStatus,
              warningValue:
                memory.policyValue?.policyResourceWarningThreshold.toFixed(2),
              criticalValue:
                memory.policyValue?.policyResourceCriticalThreshold.toFixed(2),
              policyUnit: memory.policyValue?.policyResourceUnit,
            })
          })
        })
        .finally(() => this.showLoading(false))
    },

    async getStorageList() {
      this.showLoading(true)
      this.storageList = []
      await getDashboardStorage()
        .then((response) => {
          response.forEach((storage) => {
            this.storageList.push({
              id: storage.equipmentId,
              equipmentName: storage.hostname,
              description: storage.description,
              equipmentIp: storage.ipAddress,
              currentValue:
                storage.storagePercentUsed < 0.1
                  ? "< 0.1"
                  : storage.storagePercentUsed.toFixed(1),
              status: storage.resourceStatus,
              warningValue:
                storage.policyValue?.policyResourceWarningThreshold.toFixed(2),
              criticalValue:
                storage.policyValue?.policyResourceCriticalThreshold.toFixed(2),
              policyUnit: storage.policyValue?.policyResourceUnit,
            })
          })
        })
        .finally(() => this.showLoading(false))
    },

    async getEquipmentResourceList() {
      this.showLoading(true)
      this.equipmentResourceList = []
      await getDashboardEquipmentResource()
        .then((response) => {
          response.forEach((equipmentResource) => {
            this.equipmentResourceList.push({
              id: equipmentResource.equipmentId,
              equipmentName: equipmentResource.hostname,
              tag: equipmentResource.tags,
              equipmentIp: equipmentResource.ipAddress,
              description: equipmentResource.description,
              cpuValue:
                equipmentResource.currentCpu.processorPercentUsed < 0.1
                  ? "< 0.1"
                  : equipmentResource.currentCpu.processorPercentUsed.toFixed(
                      1
                    ),
              cpuStatus: equipmentResource.currentCpu.resourceStatus,
              cpuWarningValue:
                equipmentResource.currentCpu.policyValue?.policyResourceWarningThreshold.toFixed(
                  2
                ),
              cpuCriticalValue:
                equipmentResource.currentCpu.policyValue?.policyResourceCriticalThreshold.toFixed(
                  2
                ),
              cpuPolicyUnit:
                equipmentResource.currentCpu.policyValue?.policyResourceUnit,
              memoryValue:
                equipmentResource.currentMemory.memoryPercentUsed < 0.1
                  ? "< 0.1"
                  : equipmentResource.currentMemory.memoryPercentUsed.toFixed(
                      1
                    ),
              memoryStatus: equipmentResource.currentMemory.resourceStatus,
              memoryWarningValue:
                equipmentResource.currentMemory.policyValue?.policyResourceWarningThreshold.toFixed(
                  2
                ),
              memoryCriticalValue:
                equipmentResource.currentMemory.policyValue?.policyResourceCriticalThreshold.toFixed(
                  2
                ),
              memoryPolicyUnit:
                equipmentResource.currentMemory.policyValue?.policyResourceUnit,
              storageValue:
                equipmentResource.currentStorage.storagePercentUsed < 0.1
                  ? "< 0.1"
                  : equipmentResource.currentStorage.storagePercentUsed.toFixed(
                      1
                    ),
              storageStatus: equipmentResource.currentStorage.resourceStatus,
              storageWarningValue:
                equipmentResource.currentStorage.policyValue?.policyResourceWarningThreshold.toFixed(
                  2
                ),
              storageCriticalValue:
                equipmentResource.currentStorage.policyValue?.policyResourceCriticalThreshold.toFixed(
                  2
                ),
              storagePolicyUnit:
                equipmentResource.currentStorage.policyValue
                  ?.policyResourceUnit,
            })
          })
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped>
.empty-data__icon {
  width: 100px;
  height: 100px;
}

.empty-data__text--title {
  color: #5e5873;
  font-weight: 500;
  font-size: 18px;
  margin-top: 20px;
}
</style>
